import React, { useEffect, useMemo, useRef } from "react";
import { Check, Loader2, RefreshCw, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrganizationContext } from "../../store/organizationContextSlice";
import { fetchCampaignDetails } from "../../store/outreachSlice";
import {
    formatCampaignDate,
    getCampaignProgressState,
} from "../../utils/campaignDisplayUtils";
import { getPlainTextFromHtml } from "../../utils/textValidation";
import CampaignStatusBadge from "./CampaignStatusBadge";
import FallbackEmailNotice from "./FallbackEmailNotice";

const hasValue = (value) =>
    value !== undefined && value !== null && String(value).trim() !== "";

const getDisplayValue = (value) => (hasValue(value) ? value : "Unavailable");

const getWebsiteHref = (value) => {
    if (!hasValue(value)) return "";

    const website = String(value).trim();
    const href = /^https?:\/\//i.test(website) ? website : `https://${website}`;

    try {
        const url = new URL(href);

        return ["http:", "https:"].includes(url.protocol) && url.hostname
            ? url.href
            : "";
    } catch {
        return "";
    }
};

const getLeadName = (lead = {}) => {
    const fullName = [lead.first_name, lead.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();

    return (
        lead.name ||
        lead.full_name ||
        lead.fullName ||
        fullName ||
        lead.contact_name ||
        ""
    );
};

const getLeadCompany = (lead = {}) =>
    lead.company ||
    lead.company_name ||
    lead.companyName ||
    lead.organization ||
    lead.organization_name ||
    "";

const getLeadEmail = (lead = {}) =>
    lead.email || lead.email_address || lead.work_email || "";

const getLeadDesignation = (lead = {}) => lead.designation || "";

const getLeadIdentifier = (lead = {}) =>
    lead.id || lead.lead_id || lead.linkedin_url || lead.linkedin || "";

const getLeadColumnWidthClass = (columnKey) => {
    const columnWidths = {
        lead: "w-[24%]",
        designation: "w-[22%]",
        company: "w-[54%]",
    };

    return columnWidths[columnKey] || "";
};

const getStepFields = (step = {}) => {
    const leftFields = [
        { label: "Channel", value: step.channel_type || step.channel },
        { label: "Step No", value: step.step_number },
        {
            label: "Objective",
            value:
                step.step_objective ||
                step.objective ||
                step.template_payload?.step_objective,
        },
        {
            label: "Subject",
            value: step.subject || step.template_payload?.subject,
        },
        {
            label: "Delay",
            value: hasValue(step.delay_days_from_previous)
                ? `${step.delay_days_from_previous} days`
                : "",
        },
    ].filter(({ value }) => hasValue(value));

    const rightFields = [
        {
            label: "Body / Email Content",
            value: getPlainTextFromHtml(
                step.body ||
                    step.html_body ||
                    step.template_payload?.body ||
                    step.template_payload?.html_body,
            ),
        },
    ].filter(({ value }) => hasValue(value));

    return { leftFields, rightFields };
};

const CampaignDetailsModal = ({
    isOpen,
    onClose,
    sequenceId,
    campaignSummary = null,
}) => {
    const dispatch = useDispatch();
    const requestedSequenceIdRef = useRef(null);
    const { selectedCampaign, detailsStatus, detailsError } = useSelector(
        (state) => state.outreach,
    );
    const {
        data: organizationContext,
        status: organizationContextStatus,
        error: organizationContextError,
    } = useSelector((state) => state.organizationContext);

    const isCurrentCampaign =
        selectedCampaign?.sequence_id === sequenceId ||
        selectedCampaign?.id === sequenceId;
    const hasCurrentCampaignDetails =
        isCurrentCampaign && detailsStatus === "succeeded";
    const isFailedForCurrentSequence =
        detailsStatus === "failed" &&
        requestedSequenceIdRef.current === sequenceId;
    const shouldShowLoading =
        detailsStatus === "loading" ||
        (isOpen &&
            sequenceId &&
            !isCurrentCampaign &&
            !isFailedForCurrentSequence);

    useEffect(() => {
        if (!isOpen) {
            requestedSequenceIdRef.current = null;
            return;
        }

        if (
            !sequenceId ||
            hasCurrentCampaignDetails ||
            requestedSequenceIdRef.current === sequenceId
        ) {
            return;
        }

        requestedSequenceIdRef.current = sequenceId;
        dispatch(fetchCampaignDetails(sequenceId));
    }, [dispatch, hasCurrentCampaignDetails, isOpen, sequenceId]);

    useEffect(() => {
        if (!isOpen || organizationContextStatus !== "idle") return;

        dispatch(fetchOrganizationContext());
    }, [dispatch, isOpen, organizationContextStatus]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        window.addEventListener("keydown", handleEscape);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, onClose]);

    const overviewItems = useMemo(() => {
        if (!selectedCampaign || !isCurrentCampaign) return [];

        return [
            ["Campaign name", selectedCampaign.name],
            ["Product", selectedCampaign.product_name],
            ["Search query", selectedCampaign.search_query],
            [
                "Created",
                selectedCampaign.created_at
                    ? formatCampaignDate(selectedCampaign.created_at)
                    : "",
            ],
            ["Total enrolled leads", selectedCampaign.total_leads_enrolled],
        ].filter(([, value]) => hasValue(value));
    }, [isCurrentCampaign, selectedCampaign]);

    const contextItems = useMemo(() => {
        if (!selectedCampaign || !isCurrentCampaign) return [];

        return [
            ["Campaign Objective", selectedCampaign.campaign_objective],
            [
                "Product Value Proposition",
                selectedCampaign.product_value_proposition,
            ],
            ["Tone Setting", selectedCampaign.tone_setting],
        ].filter(([, value]) => hasValue(value));
    }, [isCurrentCampaign, selectedCampaign]);
    
    const organizationContextItems = useMemo(() => {
        if (!organizationContext) return [];

        return [
            {
                label: "Company Description",
                value: organizationContext.company_description,
            },
            { label: "Brand Voice", value: organizationContext.brand_voice },
            { label: "Target ICP", value: organizationContext.target_icp },
            {
                label: "Core Value Proposition",
                value: organizationContext.core_value_proposition,
            },
            {
                label: "Website",
                value: organizationContext.website_url,
                href: getWebsiteHref(organizationContext.website_url),
            },
        ].filter((item) => hasValue(item.value));
    }, [organizationContext]);

    const steps = useMemo(
        () =>
            Array.isArray(selectedCampaign?.steps)
                ? selectedCampaign.steps
                : [],
        [selectedCampaign],
    );
    const totalSteps = steps.length;
    const progressCampaign = {
        ...(selectedCampaign || {}),
        ...(campaignSummary || {}),
        ui_helper_context: {
            ...(selectedCampaign?.ui_helper_context || {}),
            ...(campaignSummary?.ui_helper_context || {}),
        },
    };
    const { completedSteps } = getCampaignProgressState(
        progressCampaign,
        totalSteps,
    );
    const enrolledLeads = useMemo(
        () =>
            Array.isArray(selectedCampaign?.enrolled_leads)
                ? selectedCampaign.enrolled_leads
                : [],
        [selectedCampaign],
    );
    const leadColumns = useMemo(() => {
        const definitions = [
            { key: "lead", label: "Lead", getValue: getLeadName },
            {
                key: "designation",
                label: "Designation",
                getValue: getLeadDesignation,
            },
            { key: "company", label: "Company", getValue: getLeadCompany },
            { key: "email", label: "Email", getValue: getLeadEmail },
        ];

        return definitions.filter((definition) =>
            enrolledLeads.some((lead) => hasValue(definition.getValue(lead))),
        );
    }, [enrolledLeads]);

    if (!isOpen) return null;

    const handleRetry = () => {
        if (!sequenceId) return;

        requestedSequenceIdRef.current = sequenceId;
        dispatch(fetchCampaignDetails(sequenceId));
    };

    const renderStepField = (
        field,
        className = "",
        valueClassName = "",
        label,
        headerContent = null,
    ) => {
        if (!field) return null;

        return (
            <div
                className={`rounded-lg bg-[#FAFAFF] p-3 ${className}`}
            >
                <div
                    className={
                        headerContent
                            ? "flex flex-wrap items-center justify-between gap-x-3 gap-y-1"
                            : ""
                    }
                >
                    <p className="text-xs font-semibold uppercase text-slate-500">
                        {label || field.label}
                    </p>
                    {headerContent}
                </div>
                <p
                    className={`mt-1 break-words text-sm leading-5 text-slate-800 ${valueClassName}`}
                    title={String(field.value)}
                >
                    {field.value}
                </p>
            </div>
        );
    };

    return (
        <div
            className="app-fade-in fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-slate-900/40 p-4 backdrop-blur-sm"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <section
                role="dialog"
                aria-modal="true"
                aria-labelledby="campaign-details-title"
                className="app-modal-enter flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 sm:px-6">
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#0f172a]">
                            Campaign Details
                        </p>
                        <div className="mt-1 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                            <h2
                                id="campaign-details-title"
                                className="truncate text-xl font-bold text-slate-900"
                            >
                                {isCurrentCampaign && selectedCampaign?.name
                                    ? selectedCampaign.name
                                    : "Campaign details"}
                            </h2>
                            {isCurrentCampaign && selectedCampaign?.status && (
                                <CampaignStatusBadge
                                    status={selectedCampaign.status}
                                    campaign={progressCampaign}
                                    isPaused={
                                        progressCampaign.ui_helper_context
                                            ?.is_paused
                                    }
                                />
                            )}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        title="Close"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
                    >
                        <X size={20} aria-hidden="true" />
                    </button>
                </header>

                <div className="campaign-scrollbar flex-1 overflow-y-auto px-5 py-5 sm:px-6">
                    {shouldShowLoading && (
                        <div className="flex min-h-[260px] flex-col items-center justify-center rounded-xl border border-indigo-100 bg-indigo-50 text-indigo-800">
                            <Loader2
                                size={28}
                                className="animate-spin"
                                aria-hidden="true"
                            />
                            <p className="mt-3 text-sm font-semibold">
                                Loading campaign details...
                            </p>
                        </div>
                    )}

                    {!shouldShowLoading && isFailedForCurrentSequence && (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-5 text-sm text-red-700">
                            <p className="font-semibold">
                                Unable to load campaign details.
                            </p>
                            {detailsError && (
                                <p className="mt-1">{detailsError}</p>
                            )}
                            <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={handleRetry}
                                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                                >
                                    <RefreshCw size={16} aria-hidden="true" />
                                    Retry
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}

                    {!shouldShowLoading &&
                        detailsStatus === "succeeded" &&
                        isCurrentCampaign && (
                            <div className="space-y-5">
                                {overviewItems.length > 0 && (
                                    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                                        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                                            Overview
                                        </h3>
                                        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                            {overviewItems.map(
                                                ([label, value]) => (
                                                    <div
                                                        key={label}
                                                        className="rounded-lg border border-slate-100 bg-[#FAFAFF] p-3"
                                                    >
                                                        <p className="text-xs font-semibold uppercase text-slate-500">
                                                            {label}
                                                        </p>
                                                        <p
                                                            className="mt-1 break-words text-sm font-medium text-slate-900"
                                                            title={String(
                                                                value,
                                                            )}
                                                        >
                                                            {getDisplayValue(
                                                                value,
                                                            )}
                                                        </p>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </section>
                                )}

                                {contextItems.length > 0 && (
                                    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                                        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                                            Campaign Context
                                        </h3>
                                        <div className="mt-3 grid gap-3 lg:grid-cols-3">
                                            {contextItems.map(
                                                ([label, value]) => (
                                                    <div
                                                        key={label}
                                                        className="rounded-lg border border-slate-100 bg-[#FAFAFF] p-3"
                                                    >
                                                        <p className="text-xs font-semibold uppercase text-slate-500">
                                                            {label}
                                                        </p>
                                                        <p
                                                            className="mt-1 max-h-28 overflow-hidden break-words text-sm leading-5 text-slate-800"
                                                            title={String(
                                                                value,
                                                            )}
                                                        >
                                                            {value}
                                                        </p>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </section>
                                )}

                                <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                                        Organization Context
                                    </h3>

                                    {organizationContextStatus === "loading" ? (
                                        <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm font-semibold text-indigo-700">
                                            Loading organization context...
                                        </div>
                                    ) : organizationContextStatus ===
                                      "failed" ? (
                                        <div className="mt-3 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                                            <p className="font-semibold">
                                                Unable to load organization
                                                context.
                                            </p>
                                            {organizationContextError && (
                                                <p className="mt-1">
                                                    {organizationContextError}
                                                </p>
                                            )}
                                        </div>
                                    ) : organizationContextItems.length ===
                                      0 ? (
                                        <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                                            Organization context is not
                                            available.
                                        </div>
                                    ) : (
                                        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                            {organizationContextItems.map(
                                                (item) => (
                                                    <div
                                                        key={item.label}
                                                        className="rounded-lg border border-slate-100 bg-[#FAFAFF] p-3"
                                                    >
                                                        <p className="text-xs font-semibold uppercase text-slate-500">
                                                            {item.label}
                                                        </p>
                                                        {item.href ? (
                                                            <a
                                                                href={item.href}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="mt-1 block break-words text-sm font-medium text-indigo-700 hover:text-indigo-800 hover:underline"
                                                            >
                                                                {item.value}
                                                            </a>
                                                        ) : (
                                                            <p
                                                                className="mt-1 break-words text-sm leading-5 text-slate-800"
                                                                title={String(
                                                                    item.value,
                                                                )}
                                                            >
                                                                {item.value}
                                                            </p>
                                                        )}
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    )}
                                </section>

                                <section>
                                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                                        Campaign Steps
                                    </h3>
                                    {steps.length === 0 ? (
                                        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                                            No campaign steps are available yet.
                                        </div>
                                    ) : (
                                        <div className="mt-3 space-y-3">
                                            {steps.map((step, index) => {
                                                const displayStepNumber =
                                                    hasValue(step.step_number)
                                                        ? step.step_number
                                                        : index + 1;
                                                const isCompleted =
                                                    index < completedSteps;
                                                const isCompletedSegment =
                                                    index < completedSteps;
                                                const {
                                                    leftFields,
                                                    rightFields,
                                                } = getStepFields(step);
                                                const hasStepDetails =
                                                    leftFields.length > 0 ||
                                                    rightFields.length > 0;

                                                return (
                                                    <div
                                                        key={
                                                            step.step_id ||
                                                            step.id ||
                                                            `${step.step_number || "step"}-${index}`
                                                        }
                                                        className="flex gap-3"
                                                    >
                                                        <div
                                                            className="relative flex w-6 shrink-0 self-stretch py-1"
                                                            aria-hidden="true"
                                                        >
                                                            <span
                                                                className={`absolute bottom-1 left-1/2 top-1 w-1 -translate-x-1/2 rounded-full ${
                                                                    isCompletedSegment
                                                                        ? "bg-green-500"
                                                                        : "bg-slate-200"
                                                                }`}
                                                            />
                                                            {isCompletedSegment && (
                                                                <span className="absolute left-1/2 top-1/2 flex h-5 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-green-600 text-white ring-4 ring-white">
                                                                    <Check
                                                                        size={12}
                                                                        strokeWidth={3}
                                                                    />
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div
                                                            className={`min-w-0 flex-1 rounded-xl border p-4 ${
                                                                isCompleted
                                                                    ? "border-2 border-green-600 bg-white"
                                                                    : "border-slate-200 bg-white"
                                                            }`}
                                                        >
                                                            <h4 className="mb-3 text-sm font-semibold text-slate-900">
                                                                Step{" "}
                                                                {
                                                                    displayStepNumber
                                                                }
                                                            </h4>
                                                            {!hasStepDetails ? (
                                                                <p className="text-sm text-slate-600">
                                                                    No details
                                                                    are
                                                                    available
                                                                    for this
                                                                    step.
                                                                </p>
                                                            ) : (
                                                                <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
                                                                    {leftFields.length >
                                                                        0 && (
                                                                        <div className="space-y-3">
                                                                            {leftFields.map(
                                                                                (
                                                                                    field,
                                                                                ) => (
                                                                                    <React.Fragment
                                                                                        key={
                                                                                            field.label
                                                                                        }
                                                                                    >
                                                                                        {renderStepField(
                                                                                            field,
                                                                                            "",
                                                                                            field.label ===
                                                                                                "Objective"
                                                                                                ? "whitespace-pre-wrap"
                                                                                                : "",
                                                                                            undefined,
                                                                                        )}
                                                                                    </React.Fragment>
                                                                                ),
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    {rightFields.length >
                                                                        0 && (
                                                                        <div className="space-y-3">
                                                                            {rightFields.map(
                                                                                (
                                                                                    field,
                                                                                ) => (
                                                                                    <React.Fragment
                                                                                        key={
                                                                                            field.label
                                                                                        }
                                                                                    >
                                                                                        {renderStepField(
                                                                                            field,
                                                                                            "",
                                                                                            "whitespace-pre-wrap",
                                                                                            undefined,
                                                                                            field.label ===
                                                                                                "Body / Email Content" ? (
                                                                                                <FallbackEmailNotice />
                                                                                            ) : null,
                                                                                        )}
                                                                                    </React.Fragment>
                                                                                ),
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </section>

                                <section>
                                    <div className="flex items-center justify-between gap-3">
                                        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                                            Enrolled Leads
                                        </h3>
                                        {hasValue(
                                            selectedCampaign.total_leads_enrolled,
                                        ) && (
                                            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                                                Total:{" "}
                                                {
                                                    selectedCampaign.total_leads_enrolled
                                                }
                                            </span>
                                        )}
                                    </div>

                                    {enrolledLeads.length === 0 ? (
                                        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                                            No leads are enrolled in this
                                            campaign yet.
                                        </div>
                                    ) : leadColumns.length === 0 ? (
                                        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                                            Lead data is available, but no
                                            displayable lead fields were
                                            returned.
                                        </div>
                                    ) : (
                                        <div className="campaign-scrollbar mt-3 max-h-[360px] overflow-auto rounded-lg border border-slate-200">
                                            <table className="w-full table-fixed divide-y divide-slate-200 text-sm">
                                                <colgroup>
                                                    {leadColumns.map(
                                                        (column) => (
                                                            <col
                                                                key={column.key}
                                                                className={getLeadColumnWidthClass(
                                                                    column.key,
                                                                )}
                                                            />
                                                        ),
                                                    )}
                                                </colgroup>
                                                <thead className="sticky top-0 bg-slate-50">
                                                    <tr>
                                                        {leadColumns.map(
                                                            (column) => (
                                                                <th
                                                                    key={
                                                                        column.key
                                                                    }
                                                                    className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-500"
                                                                >
                                                                    {
                                                                        column.label
                                                                    }
                                                                </th>
                                                            ),
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 bg-white">
                                                    {enrolledLeads.map(
                                                        (lead, index) => (
                                                            <tr
                                                                key={
                                                                    getLeadIdentifier(
                                                                        lead,
                                                                    ) ||
                                                                    `lead-${index}`
                                                                }
                                                                className="odd:bg-slate-50 even:bg-white"
                                                            >
                                                                {leadColumns.map(
                                                                    (
                                                                        column,
                                                                    ) => {
                                                                        const value =
                                                                            column.getValue(
                                                                                lead,
                                                                            ) ||
                                                                            "Unavailable";

                                                                        return (
                                                                            <td
                                                                                key={
                                                                                    column.key
                                                                                }
                                                                                className="truncate px-4 py-3 align-middle text-slate-700"
                                                                                title={String(
                                                                                    value,
                                                                                )}
                                                                            >
                                                                                {
                                                                                    value
                                                                                }
                                                                            </td>
                                                                        );
                                                                    },
                                                                )}
                                                            </tr>
                                                        ),
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </section>
                            </div>
                        )}
                </div>
            </section>
        </div>
    );
};

export default CampaignDetailsModal;
